import { CheckCircleIcon, EyeIcon, EyeSlashIcon, XCircleIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface EmployeeInfo {
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  gym_name: string;
}

const EmployeeOnboardingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [employeeInfo, setEmployeeInfo] = useState<EmployeeInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  const verifyToken = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/users/onboarding/verify/${token}`);
      const data = await response.json();
      
      if (data.valid) {
        setEmployeeInfo(data.employee);
      } else {
        setMessage({
          type: 'error',
          text: 'Invalid or expired onboarding link. Please contact your gym administrator.'
        });
      }
    } catch (error) {
      console.error('Failed to verify token:', error);
      setMessage({
        type: 'error',
        text: 'Failed to verify onboarding link. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setMessage({
        type: 'error',
        text: 'Invalid onboarding link. Please contact your gym administrator.'
      });
      setIsLoading(false);
      return;
    }

    verifyToken();
  }, [token, verifyToken]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(null);

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setMessage({
        type: 'error',
        text: 'Passwords do not match'
      });
      setIsSubmitting(false);
      return;
    }

    // Validate password strength
    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      setMessage({
        type: 'error',
        text: passwordErrors.join(', ')
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/users/onboarding/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          password: formData.password,
          confirmPassword: formData.confirmPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        setMessage({
          type: 'success',
          text: 'Account setup completed successfully! You can now log in to the admin panel.'
        });
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setMessage({
          type: 'error',
          text: data.error || 'Failed to complete setup. Please try again.'
        });
      }
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      setMessage({
        type: 'error',
        text: 'Failed to complete setup. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleDisplayName = (role: string): string => {
    const roleMap: { [key: string]: string } = {
      'owner': 'Owner',
      'employee': 'Employee',
      'viewer': 'Viewer'
    };
    return roleMap[role] || 'Employee';
  };

  const getRoleDescription = (role: string): string => {
    const descriptions: { [key: string]: string } = {
      'owner': 'Full access to all features including billing & payments',
      'employee': 'Access to all features except billing & payments',
      'viewer': 'View-only access to all pages except billing & payments'
    };
    return descriptions[role] || 'General staff member';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-white">Verifying your invitation...</p>
        </div>
      </div>
    );
  }

  if (!employeeInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-center">
            <XCircleIcon className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-4">Invalid Invitation</h1>
            <p className="text-gray-300 mb-6">
              This invitation link is invalid or has expired. Please contact your gym administrator for a new invitation.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-6 rounded-xl transition-colors duration-200"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-400 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">
            Welcome to {employeeInfo.gym_name}!
          </h1>
          <p className="text-gray-300">
            Complete your employee account setup
          </p>
        </div>

        {/* Employee Info Card */}
        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Your Account Details</h2>
          <div className="space-y-3">
            <div>
              <span className="text-gray-300">Name:</span>
              <span className="text-white ml-2 font-medium">
                {employeeInfo.first_name} {employeeInfo.last_name}
              </span>
            </div>
            <div>
              <span className="text-gray-300">Email:</span>
              <span className="text-white ml-2 font-medium">{employeeInfo.email}</span>
            </div>
            <div>
              <span className="text-gray-300">Role:</span>
              <span className="text-indigo-300 ml-2 font-medium">
                {getRoleDisplayName(employeeInfo.role)}
              </span>
            </div>
            <div>
              <span className="text-gray-300">Gym:</span>
              <span className="text-white ml-2 font-medium">{employeeInfo.gym_name}</span>
            </div>
          </div>
          <div className="mt-4 p-3 bg-indigo-500/20 rounded-lg">
            <p className="text-sm text-indigo-200">
              {getRoleDescription(employeeInfo.role)}
            </p>
          </div>
        </div>

        {/* Password Setup Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Create Your Password</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-300 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 focus:bg-white/20 transition-all duration-200 pr-12"
                    placeholder="Enter your new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-300 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 focus:bg-white/20 transition-all duration-200 pr-12"
                    placeholder="Confirm your new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="mt-4 p-4 bg-gray-800/50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-300 mb-2">Password Requirements:</h4>
              <ul className="text-xs text-gray-400 space-y-1">
                <li>• At least 8 characters long</li>
                <li>• Contains uppercase and lowercase letters</li>
                <li>• Contains at least one number</li>
                <li>• Contains at least one special character</li>
              </ul>
            </div>
          </div>

          {/* Message */}
          {message && (
            <div className={`rounded-xl p-4 backdrop-blur-sm border ${
              message.type === 'success' 
                ? 'bg-green-500/20 border-green-400/30 text-green-200' 
                : 'bg-red-500/20 border-red-400/30 text-red-200'
            }`}>
              {message.text}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-medium py-4 px-6 rounded-xl transition-all duration-200 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed shadow-lg"
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Setting up your account...
              </div>
            ) : (
              'Complete Setup & Activate Account'
            )}
          </button>
        </form>

        {/* Security Note */}
        <div className="text-center">
          <p className="text-sm text-gray-400">
            🔒 Your account will be activated once you complete this setup. 
            This link will expire after use for security purposes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmployeeOnboardingPage;
